import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ReduxStates } from '../../../../redux/reduxTypes';

import { fetchCall } from '../../../../services/api';

import { returnFormattedSegmentFilters } from '../../../../helpers/format';
import { ModalFilter, FilterOption as QueriedFilterOption } from '../../../../types';
import { ReactComponent as ExitIcon } from '../../../../assets/icons/exit-icon.svg';

interface Props {
  filters: Array<ModalFilter | null>;
  label: string;
  subLabel?: string;
  onChange: (values: ModalFilter | null) => void;
  values: Array<QueriedFilterOption>;
  viewingDefault?: boolean;
  // setViewingDefault?: (option: boolean) => void;
  staticDefault?: boolean;
  required?: boolean;
  focus?: boolean;
}

interface VariantProps {
  [key: string]: {
    [key: string]: string;
  };
}

const variantStyles: VariantProps = {
  wrapper: {
    inactive: 'bg-[#f3f3f3]',
    default: ''
  },
  text: {
    inactive: 'text-[#898989]',
    default: 'text-navy'
  },
  staticWrapper: {
    inactive: 'bg-[#f3f3f3]',
    default: 'border border-[#b9b9b9] '
  },
  staticText: {
    inactive: 'text-[#898989]',
    default: 'text-navy bg-opaqueNavy'
  }
};

const FilterOption = ({
  viewingDefault,
  // setViewingDefault,
  label,
  subLabel,
  onChange,
  values,
  filters,
  staticDefault,
  required,
  focus
}: Props): React.ReactElement => {
  const shareSegmentArray = ['1P', '2P', '3P'];

  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const config = useSelector((state: ReduxStates) => state.config);

  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [allowSearch, setAllowSearch] = useState<boolean>(true);
  const [selectedShareSegments, setSelectedShareSegments] = useState<Array<string>>(values?.map((val) => val?.name));
  const [queriedOptions, setQueriedOptions] = useState<Array<QueriedFilterOption>>([]);
  const [campaignTypeOptions, setCampaignTypeOptions] = useState<Array<QueriedFilterOption>>([]);
  const options = label === 'Campaign Type' ? campaignTypeOptions : queriedOptions;

  const inputEl: any = useRef(null);

  useEffect(() => {
    setSelectedShareSegments(values?.map((val) => val.name));
  }, [viewingDefault]);

  const removeSelectedFilter = (index: number) => {
    if (!allowSearch) setAllowSearch(true);

    const updatedFilters = values?.filter((filter: QueriedFilterOption, filterIndex: number) => index !== filterIndex);
    // if updated filter is empty, bubble up null value to clear
    onChange(updatedFilters?.length > 0 ? { name: label, values: updatedFilters } : null);
  };

  useEffect(() => {
    const handleClick = (e: any) => {
      const targetElementId = e.target.id;

      if (isFocused && targetElementId !== label) {
        setIsFocused(false);
      }
    };
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    };
  });

  const handleOptionFetching = (input: string) => {
    if (input === '') {
      const filterParams = returnFormattedSegmentFilters(filters);
      const params = { ...filterParams, startsWith: input };

      const endpoint = label.toLowerCase().replace(' ','');

      fetchCall(`${config.react_app_api_base_url}/filter/${endpoint}`, params)
        .then((options) => {
          if (input === '' && options.length === values?.length) {
            setAllowSearch(false);
            return setIsFocused(false);
          } else {
            setAllowSearch(true);
            setIsFocused(true);
          }
          if (label === 'Campaign Type' && !campaignTypeOptions.length) {
            setCampaignTypeOptions(options);
          } else {
            setQueriedOptions(options);
          }
          setIsFocused(true);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  useEffect(() => {
    // setViewingDefault && setViewingDefault(false);

    if (label === 'Share') {
      // always default to 1P selected
      if (selectedShareSegments?.length === 0) {
        setSelectedShareSegments(['1P']);
      }
      const formattedSegments = selectedShareSegments?.map((segment) => ({ id: segment, name: segment }));
      onChange({ name: label, values: formattedSegments });
    }
  }, [selectedShareSegments]);

  const handleShareUpdate = (share: string) => {
    // if selected share is already active
    if (selectedShareSegments?.includes(share)) {
      // if there is only one selected share, don't deselect it
      if (selectedShareSegments?.length <= 1) return;
      const updatedSegments = selectedShareSegments.filter((segment) => segment != share);
      return setSelectedShareSegments(updatedSegments);
    }
    setSelectedShareSegments((prev) => [...prev, share]);
  };

  const baseItemStyles =
    'box-border border text-left block w-full border-0 bg-white text-sm py-[17px] px-2.5 text-nunitoMedium';
  return (
    <div className="relative mb-6">
      <div className="flex justify-between mb-2">
        <div className="text-xs leading-4 text-[#2b2b2b] tracking-[0.1px] uppercase m-0 font-nunitoBold">
          {`${label} ${label === 'Channel' ? '(Limit 1)' : ''}`}
          {required ? (
            <span className="text-xs text-red-500 capitalize ml-[2px]">
              * &nbsp; {focus ? 'This field is required' : ''}
            </span>
          ) : null}
          {subLabel ? (
            <p className={`text-[10px] text-[#5B5B5B] leading-3 normal-case font-nunitoLightItalic mt-1`}>{subLabel}</p>
          ) : null}
        </div>
        {label !== 'Share' && !staticDefault && (
          <p
            className={`m-0 text-navy font-nunitoBold text-sm cursor-pointer leading-[19.1px] ${
              values?.length > 0 ? 'visible pointer-events-auto' : 'invisible pointer-events-none'
            } `}
            onClick={() => {
              setAllowSearch(true);
              onChange(null);
            }}
          >
            {copyData?.clear_button}
          </p>
        )}
      </div>
      {label === 'Share' ? (
        <div className="flex my-3 mx-0">
          {shareSegmentArray?.map((share) => {
            return (
              <div
                id={`filter_button_share-${share}`}
                className={`cursor-pointer border border-borderGray rounded w-full text-center mr-[9px] py-[6.5px] px-0 last:mr-0 ${
                  variantStyles.wrapper[selectedShareSegments?.includes(share) ? '' : 'inactive']
                }`}
                key={share}
                onClick={() => handleShareUpdate(share)}
              >
                <span
                  className={`font-light font-nunitoLight tracking-[0.1px] ${
                    variantStyles.text[selectedShareSegments?.includes(share) ? 'default' : 'inactive']
                  }`}
                >
                  {share}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          id={`filter_dropdown_${label.replace(/ /g, '').toLowerCase()}`}
          className={`box-border ${
            focus ? 'border-red-500' : ''
          } items-center py-1 px-1.5 rounded w-full max-h-[200px] overflow-x-hidden overflow-y-scroll no-scrollbar flex flex-wrap gap-x-2 gap-y-2 ${
            variantStyles.staticWrapper[staticDefault ? 'inactive' : 'default']
          }`}
        >
          {values?.map((filter: QueriedFilterOption, index: number) => {
            return (
              <div
                className={`rounded py-[6.5px] px-2 text-sm font-nunitoBold leading-[21px] flex items-center w-max ${
                  variantStyles.staticText[staticDefault ? 'inactive' : 'default']
                }`}
                key={index}
              >
                {filter.name}
                {!staticDefault && (
                  <ExitIcon
                    className="h-2.5 cursor-pointer ml-[5px] fill-current text-navy"
                    onClick={() => removeSelectedFilter(index)}
                  />
                )}
              </div>
            );
          })}
          {!staticDefault && (
            <input
              className="border-0 w-auto text-sm  font-nunitoRegular leading-[21px] focus:outline-none min-h-[35px] disabled:hidden"
              id={label}
              ref={inputEl}
              onClick={() => handleOptionFetching(inputValue)}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={label === 'Channel' && values?.length > 0}
              placeholder={copyData?.type_to_filter}
              autoComplete="off"
            />
          )}
        </div>
      )}
      {isFocused && allowSearch && (
        <div className="bg-white shadow-listContainerShadow rounded absolute box-border max-h-[215px] overflow-scroll no-scrollbar w-full top-[105%] left-0 z-10 p-[3px]">
          {options?.length > 0 ? (
            options?.map((option: QueriedFilterOption, index: number) => {
              // if input value matches option value && is option value isn't already chosen
              if (
                option.name.toLowerCase().lastIndexOf(inputValue.toLowerCase(), 0) === 0 &&
                !values?.some((val) => val.name === option.name)
              ) {
                return (
                  <button
                    className={`${baseItemStyles} cursor-pointer last:border-0 hover:outline-none hover:bg-opaqueNavy hover:text-navy focus:outline-none focus:bg-opaqueNavy focus:text-navy`}
                    id={`${label}-option-button`}
                    key={index}
                    onClick={() => {
                      const newValues = values ? [...values, option] : [option];
                      const newValueObject = { name: label, values: newValues };
                      setInputValue('');
                      onChange(newValueObject);

                      if (newValueObject.values?.length === options.length || label === 'Channel') {
                        setIsFocused(false);
                      } else {
                        inputEl.current.focus();
                      }
                    }}
                  >
                    {option.name}
                  </button>
                );
              }
            })
          ) : (
            <p className={`${baseItemStyles} py-[5px] px-2.5`}>{copyData?.no_results_found}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterOption;
